import {
  AdminInput,
  AdminSpinner,
  Button,
  CopyIcon,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  Icon,
  Img,
  Modal,
  P,
  PropsModal,
  ProviderLogoWrapper,
  Separator,
  SuccessToast,
  UnpaidReportOrdersModal,
  connectLocationWithProvider,
  connectProvider,
  copyToClipboard,
  disconnectBrandProvider,
  disconnectLocationWithProvider,
  isAdmin,
  isFoodHubProviderConnection,
  isUrlValid,
  pauseBrandProvider,
  pauseProvider,
  renderProviderImage,
  renderProviderName,
  transferToGrubhubPos,
  transferToGrubhubPosBrand,
  unpauseBrandProvider,
  unpauseProvider,
  updateStoreLink,
  useIsIOSSafari,
  useSafeWindowOpen,
  useUnpaidReportOrders,
  reportError,
} from '@vgrubs/components';
import {
  ProviderAndProviderStatusResponse,
  ProviderConnectionType,
} from 'components/src/types/api/location.types';
import { BrandProviderConfigEntity } from 'components/src/types/brand.types';
import {
  Provider,
  ProviderConnectionStatus,
  ProviderStatus,
} from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { pausedUntilLabel, scheduledPauseStartLabel } from './providerPause';
import { ProviderPauseOptions } from './ProviderPauseOptions';
import { ScheduledPauseCards } from './ScheduledPauseCards';
import { useProviderPause } from './useProviderPause';
import { ScheduledPausesState } from './useScheduledPauses';

export const ProviderDetailsModal = ({
  modalIsOpen,
  closeModal,
  id,
  provider,
  pathPrefix,
  statuses,
  setStatuses,
  status,
  credentials,
  getProvidersStatuses,
  setSetupMenuToContinueModal,
  connectionType,
  timeZone,
  scheduled,
}: PropsModal & {
  modalIsOpen: boolean;
  provider: Provider;
  id: string | number;
  pathPrefix: 'locations' | 'brands';
  status:
    | {
        provider: Provider;
        status: ProviderStatus;
        connectionStatus: ProviderConnectionStatus;
        haveMenuSet?: boolean | null | undefined;
        haveWorkingHoursSet?: boolean | null | undefined;
        inProgressConnectAllowed: boolean;
        storeLink: string | null;
        pausedUntil?: string | null;
        id: number;
      }
    | undefined;
  statuses: ProviderAndProviderStatusResponse;
  setStatuses: (value: ProviderAndProviderStatusResponse) => void;
  credentials: BrandProviderConfigEntity | undefined;
  getProvidersStatuses: (silent?: boolean) => void;
  setSetupMenuToContinueModal: Dispatch<SetStateAction<boolean>>;
  connectionType: ProviderConnectionType | undefined;
  /** The location's; the pause end is picked and shown on its clock. */
  timeZone?: string;
  scheduled: ScheduledPausesState;
}) => {
  const pause = useProviderPause(modalIsOpen, timeZone);
  const scheduledPauses = scheduled.of(provider);
  const [credentialsModal, setCredentialsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transferToGrubhubPosModal, setTransferToGrubhubPosModal] =
    useState(false);
  const disconnectFunction =
    pathPrefix === 'locations'
      ? disconnectLocationWithProvider
      : disconnectBrandProvider;

  const [storeLinkModal, setStoreLinkModal] = useState(false);
  const unpaidReportOrders = useUnpaidReportOrders();
  const [storeLink, setStoreLink] = useState<string | null>(null);

  useEffect(() => {
    if (status?.storeLink) {
      setStoreLink(status.storeLink);
    } else {
      setStoreLink('');
    }
  }, [status?.storeLink]);

  const pausedUntil = pausedUntilLabel(status?.pausedUntil, timeZone);

  // A merchant hears nothing when a scheduled pause starts, so the list is
  // fetched again each time the dialog opens.
  useEffect(() => {
    if (modalIsOpen) scheduled.refresh();
  }, [modalIsOpen]);

  async function handlePause(action: 'pause' | 'unpause') {
    if (pause.error) return ErrorToast(pause.error);
    const { length } = pause;
    const pauseFunction =
      pathPrefix === 'locations'
        ? () => pauseProvider(id, { provider, ...length })
        : () => pauseBrandProvider(id, { provider, ...length });
    const unpauseFunction =
      pathPrefix === 'locations'
        ? () => unpauseProvider(id, provider)
        : () => unpauseBrandProvider(id, provider);

    const service = action === 'unpause' ? unpauseFunction : pauseFunction;

    try {
      setLoading(true);
      const res = await service();

      if (action === 'pause' && pause.isScheduled) {
        // Nothing changes on the provider until the pause starts.
        await scheduled.refresh();
        SuccessToast(`Pause scheduled: ${pause.rangeLabel}`);
      } else {
        await getProvidersStatuses(true);
        SuccessToast(res.data.message);
      }
      closeModal?.();
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleDisconnect(confirmUnpaidOrders?: boolean) {
    try {
      setLoading(true);
      const res = await disconnectFunction(id, provider, confirmUnpaidOrders);
      await getProvidersStatuses(true);
      SuccessToast(res.data.message);
      closeModal?.();
      setLoading(false);
    } catch (error) {
      // Uber Eats / Grubhub orders still waiting for their payment report:
      // an admin confirms paying them out from the subtotal, everyone else
      // gets the backend's contact-support message as an ordinary error.
      if (!unpaidReportOrders.intercept(error, () => handleDisconnect(true)))
        reportError(error);
      setLoading(false);
    }
  }

  const connectFunction =
    pathPrefix === 'locations' ? connectLocationWithProvider : connectProvider;

  const openSafe = useSafeWindowOpen();
  const isIOSSafari = useIsIOSSafari();

  const [url, setUrl] = useState<string | null>(null);

  const handleRedirect = (url: string, isNewTab: boolean) => {
    openSafe(url, {
      immediate: true,
      target: isNewTab ? '_blank' : '_self',
    });

    closeModal?.();
    setUrl(null);
  };

  const localhost = true;

  async function handleContinueConnection(shouldUseFoodhub: boolean) {
    if (
      provider !== Provider.grubhubPos &&
      (!status?.haveMenuSet || !status?.haveWorkingHoursSet)
    ) {
      closeModal?.();
      setSetupMenuToContinueModal(true);
      return;
    }

    try {
      setLoading(true);
      const res = await connectFunction(id, {
        provider,
        storeId: null,
        shouldUseFoodhub: shouldUseFoodhub ? true : undefined,
        redirectUrl: shouldUseFoodhub
          ? undefined
          : `${
              localhost ? process.env.NX_ADMIN_URL : window.location.origin
            }/${pathPrefix}/manage-providers/${id}?cp=${provider}`,
      });

      setLoading(false);
      const url = res.data.data;

      if (isIOSSafari) {
        setUrl(url);
      } else {
        handleRedirect(url, shouldUseFoodhub);
      }
    } catch (error) {
      setLoading(false);
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const hasCompletedMenuWorkingHours =
    status?.haveMenuSet === true && status?.haveWorkingHoursSet === true;

  const transferToGrubhubPosFunction =
    pathPrefix === 'locations'
      ? transferToGrubhubPos
      : transferToGrubhubPosBrand;

  const handleTransferToGrubhubPos = async () => {
    try {
      setLoading(true);
      const { data } = await transferToGrubhubPosFunction(id, provider);
      await getProvidersStatuses(true);
      SuccessToast(data.message);
      setTransferToGrubhubPosModal(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStoreLink = async () => {
    if (storeLink !== '' && !isUrlValid(storeLink ?? '')) {
      return ErrorToast(
        'Please enter a valid store link (e.g. https://www.example.com)'
      );
    }
    if (storeLink === status?.storeLink) {
      return ErrorToast('Change the store link to update it');
    }
    try {
      setLoading(true);
      const { data } = await updateStoreLink(status?.id ?? '', storeLink ?? '');
      setStatuses(
        statuses.map((s) =>
          s.id === status?.id ? { ...s, storeLink: storeLink ?? null } : s
        )
      );
      SuccessToast(data.message);
      setStoreLinkModal(false);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <AdminSpinner />}

      <Modal
        isOpen={modalIsOpen && !storeLinkModal && !unpaidReportOrders.pending}
        closeModal={closeModal}
        logo={false}
        header="Provider Details"
      >
        <Div $flex $center>
          <ProviderLogoWrapper>
            <span>
              <Img alt="" src={renderProviderImage(provider)} />
            </span>
          </ProviderLogoWrapper>
        </Div>

        <ScheduledPauseCards
          pauses={scheduledPauses}
          timeZone={timeZone}
          onCancel={scheduled.cancel}
        />

        {status?.status === ProviderStatus.online &&
          provider !== Provider.nabe && (
            <>
              <H3 $mb="16">Pause</H3>
              <ProviderPauseOptions pause={pause} timeZone={timeZone} />

              {scheduledPauses[0] && !pause.isScheduled && (
                <Disclaimer $m="16px 0 0 0" $center>
                  Store stays online until{' '}
                  {scheduledPauseStartLabel(scheduledPauses[0], timeZone)}.
                </Disclaimer>
              )}

              <Button
                $selected
                $w="100%"
                $mt="16"
                onClick={() => handlePause('pause')}
              >
                {pause.confirmLabel ?? 'Confirm Pause'}
              </Button>
            </>
          )}

        {status?.status === ProviderStatus.offline && (
          <>
            <H5>
              {pausedUntil
                ? `${pausedUntil}. It comes back on by itself then, or click on
                  the resume button to unpause it now.`
                : `The provider is currently paused. Click on the resume button
                  to unpause it.`}
            </H5>
            <Button
              $selected
              $w="100%"
              $mt="16"
              onClick={() => handlePause('unpause')}
            >
              Resume
            </Button>
          </>
        )}

        <H5 $mb="24">
          {status?.status === ProviderStatus.online &&
            provider === Provider.nabe &&
            status?.connectionStatus === ProviderConnectionStatus.connected &&
            `The provider is connected. You may disconnect it if it’s no longer
              needed.`}

          {status?.connectionStatus === ProviderConnectionStatus.rejected &&
            `The provider has rejected connection. You may disconnect it if it’s
            no longer needed.`}
          {status?.connectionStatus === ProviderConnectionStatus.waiting &&
            `The provider is pending connection. You may disconnect it if it’s no
            longer needed.`}
          {!isFoodHubProviderConnection(provider) &&
            status?.connectionStatus === ProviderConnectionStatus.inProgress &&
            `The provider is in progress. You may disconnect it if it’s no
            longer needed.`}
          {status?.connectionStatus === ProviderConnectionStatus.waitingMenu &&
            `The provider is waiting for a menu. You may disconnect it if it’s no
            longer needed.`}
          {status?.connectionStatus === ProviderConnectionStatus.disabled &&
            `The provider is disabled. You may disconnect it if it’s no longer
            needed.`}

          {isFoodHubProviderConnection(provider) &&
            !hasCompletedMenuWorkingHours &&
            connectionType === ProviderConnectionType.foodhub &&
            `The provider is in progress. Make sure working hours and menu
                are submitted before proceeding.`}

          {isFoodHubProviderConnection(provider) &&
            hasCompletedMenuWorkingHours &&
            status?.connectionStatus !== ProviderConnectionStatus.connected &&
            connectionType === ProviderConnectionType.foodhub && (
              <>
                The provider connection is in progress.
                <br /> Click "Continue Connection" if setup is not complete or
                wait while the integration is being reviewed and approved.
              </>
            )}
        </H5>

        <Div $flex $column $gap="8px">
          {credentials && (
            <Button
              $selected
              $yellow
              $w="100%"
              onClick={() => setCredentialsModal(true)}
            >
              Show Credentials
            </Button>
          )}

          {!url &&
            isFoodHubProviderConnection(provider) &&
            connectionType === ProviderConnectionType.foodhub &&
            status?.connectionStatus !== ProviderConnectionStatus.connected &&
            provider !== Provider.grubhub && (
              <Button
                $selected
                $w="100%"
                onClick={() => handleContinueConnection(true)}
              >
                Continue Connection
              </Button>
            )}

          {!url &&
            isFoodHubProviderConnection(provider) &&
            connectionType === ProviderConnectionType.foodhub &&
            provider === Provider.grubhub && (
              <Button
                $selected
                $w="100%"
                onClick={() => handleContinueConnection(true)}
              >
                Continue Connection
              </Button>
            )}

          {!url &&
            provider === Provider.grubhubPos &&
            status?.connectionStatus ===
              ProviderConnectionStatus.waitingMenu && (
              <Button
                $selected
                $w="100%"
                onClick={() => handleContinueConnection(false)}
              >
                Continue Connection
              </Button>
            )}

          {url && (
            <Button
              $selected
              $w="100%"
              onClick={() => handleRedirect(url, true)}
            >
              Proceed to authorization page
            </Button>
          )}

          {isAdmin &&
            provider === Provider.grubhub &&
            connectionType === ProviderConnectionType.thirdParty && (
              <Button
                $selected
                $gray
                $w="100%"
                onClick={() => {
                  setTransferToGrubhubPosModal(true);
                  closeModal?.();
                }}
              >
                Transfer to Grubhub POS
              </Button>
            )}

          {isAdmin && (
            <Button $w="100%" $gray onClick={() => setStoreLinkModal(true)}>
              Store Link
            </Button>
          )}

          <Button $selected $gray $w="100%" onClick={() => handleDisconnect()}>
            Disconnect
          </Button>
        </Div>
      </Modal>
      <CredentialsModal
        credentials={credentials}
        modalIsOpen={credentialsModal}
        closeModal={() => {
          setCredentialsModal(false);
        }}
      />
      <TransferToGrubhubPosModal
        transferToGrubhubPosFunction={handleTransferToGrubhubPos}
        modalIsOpen={transferToGrubhubPosModal}
        closeModal={() => {
          setTransferToGrubhubPosModal(false);
        }}
      />
      <StoreLinkModal
        storeLink={storeLink}
        setStoreLink={setStoreLink}
        handleUpdateStoreLink={handleUpdateStoreLink}
        modalIsOpen={storeLinkModal}
        closeModal={() => {
          setStoreLinkModal(false);
        }}
        provider={provider}
      />
      <UnpaidReportOrdersModal
        pending={unpaidReportOrders.pending}
        closeModal={unpaidReportOrders.close}
      />
    </>
  );
};

const StoreLinkModal = ({
  modalIsOpen,
  closeModal,
  storeLink,
  setStoreLink,
  handleUpdateStoreLink,
  provider,
}: PropsModal & {
  modalIsOpen: boolean;
  storeLink: string | null | undefined;
  setStoreLink: Dispatch<SetStateAction<string | null>>;
  handleUpdateStoreLink: (deleteStoreLink: boolean) => void;
  provider: Provider;
}) => {
  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`Store Link (${renderProviderName(provider)})`}
    >
      <AdminInput
        label="Store Link"
        value={storeLink ?? ''}
        onChange={(e) => setStoreLink(e.target.value)}
        $textCenter
        placeholder="https://www.example.com"
      />

      <Div $flex $gap="16px" $mt="20">
        <Button
          $selected
          $w="100%"
          onClick={() => handleUpdateStoreLink(false)}
        >
          Save
        </Button>
      </Div>
    </Modal>
  );
};

const CredentialsModal = ({
  modalIsOpen,
  closeModal,
  credentials,
}: PropsModal & {
  modalIsOpen: boolean;
  credentials: BrandProviderConfigEntity | undefined;
}) => {
  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header="Credentials"
      zIndex={200}
      textAlign={'left'}
    >
      <Div $flex $alignCenter $between>
        <div>
          <H5 $m="0">Login</H5>
          <P $m="0">{credentials?.username}</P>
        </div>
        <Icon $pointer onClick={() => copyToClipboard(credentials?.username)}>
          <CopyIcon />
        </Icon>
      </Div>
      <Separator $m="20px 0" />

      <Div $flex $alignCenter $between>
        <div>
          <H5 $m="0">Password</H5>
          <P $m="0">{credentials?.password}</P>
        </div>
        <Icon $pointer onClick={() => copyToClipboard(credentials?.password)}>
          <CopyIcon />
        </Icon>
      </Div>
      <Separator $m="20px 0" />

      <Div $flex $alignCenter $between>
        <div>
          <H5 $m="0">Store ID</H5>
          <P $m="0">{credentials?.storeId}</P>
        </div>
        <Icon $pointer onClick={() => copyToClipboard(credentials?.storeId)}>
          <CopyIcon />
        </Icon>
      </Div>
    </Modal>
  );
};

const TransferToGrubhubPosModal = ({
  modalIsOpen,
  closeModal,
  transferToGrubhubPosFunction,
}: PropsModal & {
  modalIsOpen: boolean;
  transferToGrubhubPosFunction: () => void;
}) => {
  return (
    <Modal
      isOpen={modalIsOpen}
      closeModal={closeModal}
      logo={false}
      textAlign={'left'}
    >
      <Div $flex $center>
        <H5 $center $maxWidth="340px">
          The Grubhub integration will be switched to the Grubhub POS so that
          you can connect the Foodhub–Grubhub integration.
        </H5>
      </Div>
      <Div>
        <Button
          $selected
          $w="100%"
          onClick={transferToGrubhubPosFunction}
          $mt="8"
        >
          Transfer to Grubhub POS
        </Button>
        <Button $selected $gray $w="100%" onClick={closeModal} $mt="8">
          Cancel
        </Button>
      </Div>
    </Modal>
  );
};
